import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { WarningIcon as Warning, XIcon as X, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Pre-sale Stripe Tax readiness banner.
 *
 * Renders ONLY when:
 *   1. The seller has a connected Stripe account (`connected: true`)
 *   2. Payouts are actually working (`payouts_enabled: true`)
 *   3. Tax is NOT fully configured (`tax_configured: false`)
 *   4. The seller hasn't dismissed it this session
 *
 * We hide before Stripe Connect onboarding because there's already
 * a louder "Connect Stripe" banner higher up the page in those
 * cases — stacking warnings creates noise and trains sellers to
 * ignore them.
 *
 * Dismissal is per-session via sessionStorage (not localStorage)
 * so the banner reappears next login. This is intentional: a
 * seller who hasn't actually configured Tax should see this every
 * day until they do, but not on every page-load within a session.
 *
 * Usage:
 *   <StripeTaxStatusBanner persona="storefront" />
 *   <StripeTaxStatusBanner persona="breeder" />
 *
 * The `persona` prop only affects the copy — endpoint is shared.
 */
const COPY = {
  storefront: {
    title: "Stripe Tax isn't on yet",
    body: "3 quick steps and every sale collects the right sales tax automatically. One-time setup.",
  },
  breeder: {
    title: "Stripe Tax isn't on yet",
    body: "CA taxes dog sales. 3 quick steps and every escrow checkout collects it automatically. One-time setup.",
  },
  provider: {
    title: "Stripe Tax isn't on yet",
    body: "3 quick steps and any taxable add-ons you sell collect the right tax automatically. One-time setup.",
  },
};

export default function StripeTaxStatusBanner({ persona = "storefront" }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Read sessionStorage on mount so dismissal persists within the
  // session but resets next login.
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem(`tax_banner_dismissed_${persona}`);
      if (flag === "1") setDismissed(true);
    } catch {
      // sessionStorage can throw in private mode / SSR — ignore.
    }
  }, [persona]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/me/tax-status");
        if (!cancelled) setStatus(r.data);
      } catch {
        // Endpoint failures shouldn't crash the page; treat as
        // "unknown" → banner stays hidden.
        if (!cancelled) setStatus({ connected: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(`tax_banner_dismissed_${persona}`, "1");
    } catch {
      // ignore
    }
  };

  // Gate everything before render.
  if (!status) return null;
  if (dismissed) return null;
  if (status.transient) return null;             // transient Stripe error → say nothing
  if (!status.connected) return null;            // Stripe Connect not done yet — louder banner already shows
  if (!status.payouts_enabled) return null;      // ditto
  if (status.tax_configured) return null;        // already set up — congrats, no banner

  const copy = COPY[persona] || COPY.storefront;

  return (
    <div
      className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
      data-testid={`tax-status-banner-${persona}`}
      role="status"
    >
      <Warning size={22} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-amber-900 text-[14px]">{copy.title}</p>
        <p className="text-[13px] text-amber-900/90 mt-1 leading-relaxed">{copy.body}</p>
        <p className="text-[11.5px] text-amber-900/70 mt-1 leading-relaxed">
          As the seller of record, collecting and filing sales tax is your responsibility — Armstrong K9 Registry never handles it for you.
        </p>
        <Link
          to="/help/stripe-tax-setup"
          className="inline-flex items-center gap-1.5 mt-3 bg-amber-900 hover:bg-ink text-white rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors"
          data-testid={`tax-status-cta-${persona}`}
        >
          Set it up now
          <ArrowRight size={13} weight="bold" />
        </Link>
      </div>
      <button
        onClick={dismiss}
        className="text-amber-700 hover:text-amber-900 flex-shrink-0 -mr-1"
        aria-label="Dismiss for this session"
        data-testid={`tax-status-dismiss-${persona}`}
      >
        <X size={18} weight="bold" />
      </button>
    </div>
  );
}
